import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useDispatch } from 'react-redux';

import {
  createStyles,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Theme,
  withStyles,
  WithStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import { Button, ErrorBoundary } from 'core';

import { useCurrentUser } from '@lib/hooks';

import { TabsChatActivitiesTab } from 'deprecated/fragments';
import { QueryChat, WrapperQueryListChatActivities } from 'deprecated/queries';

import { signOut } from 'actions';
import { IconButton, Toolbar, ToolbarNavigation, Typography } from 'components/v2';

const styles = ({ spacing: { unit } }: Theme) =>
  createStyles({
    root: {
      height: '100%',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    gap: {
      flex: 1,
    },
    wrapperQueryListChatActivities: {
      flex: 1,
      overflow: 'hidden',
    },
    closeButton: {
      marginRight: -1 * unit * 2,
    },
  });

const messages = defineMessages({
  title: {
    id: 'deprecated.query-triggers.HubJr.title',
    defaultMessage: 'Chats',
  },
});

export interface HubJrProps extends WithStyles<typeof styles> {
  selectedPatient?: string | null;
  includeCurrentUser?: boolean;
  onPatientSelect?: (patientId?: string) => void;
  onClose?: () => void;
}

const HubJr: React.FC<HubJrProps> = ({
  classes,
  selectedPatient: selectedPatientProp = null,
  includeCurrentUser,
  onPatientSelect,
  onClose,
}) => {
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const [selectedTab, setSelectedTab] = useState(TabsChatActivitiesTab.MyChats);
  const [selectedPatient, setSelectedPatient] = useState<string | null>(selectedPatientProp);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { formatMessage } = useIntl();
  const directMessage = (window as any).directMessage;

  const handlePatientSelected = useCallback(
    (patientId?: string) => {
      if (onPatientSelect) {
        onPatientSelect(patientId);
      }
      setSelectedPatient(patientId || null);
    },
    [onPatientSelect],
  );

  useEffect(() => {
    if (directMessage) {
      handlePatientSelected(directMessage.patientId);
    }
  }, [directMessage, handlePatientSelected]);

  return (
    <div className={classes.root}>
      {selectedPatient ? (
        <ErrorBoundary>
          <Suspense
            fallback={
              <ToolbarNavigation
                variant="dense"
                color="supportSlate"
                onBack={() => handlePatientSelected()}
                onClose={onClose}
              />
            }
          >
            <QueryChat
              variant="popper"
              source="hub"
              shouldHideSenderSelector
              patientId={selectedPatient}
              onBack={() => handlePatientSelected()}
              onClose={onClose}
            />
          </Suspense>
        </ErrorBoundary>
      ) : (
        <>
          <Toolbar variant="dense" color="supportSlate">
            <Typography variant="body1LargeStrongBold" color="inherit">
              {formatMessage(messages.title)}
            </Typography>
            <div className={classes.gap} />
            {onClose && (
              <IconButton className={classes.closeButton} onClick={onClose} data-lh-id="hubJr-closeButton">
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
            {includeCurrentUser && currentUser && (
              <div>
                <Button color="inherit" onClick={(event) => setAnchorEl(event.currentTarget)}>
                  {currentUser.name}
                </Button>
                <Menu id="basic-menu" anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                  <MenuItem
                    onClick={() => {
                      dispatch(signOut({}));
                      setAnchorEl(null);
                    }}
                  >
                    <ListItemIcon>
                      <ExitToAppIcon />
                    </ListItemIcon>
                    <ListItemText inset primary="Logout" />
                  </MenuItem>
                </Menu>
              </div>
            )}
          </Toolbar>
          <WrapperQueryListChatActivities
            className={classes.wrapperQueryListChatActivities}
            variant="popper"
            selectedTab={selectedTab}
            onTabChange={setSelectedTab}
            selectedPatient={selectedPatient}
            onPatientSelect={(value) => handlePatientSelected(value)}
            automaticSelectionEnabled={false}
            includeFilters={false}
          />
        </>
      )}
    </div>
  );
};

export default withStyles(styles)(HubJr);
